import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
    useTheme,
} from '@mui/material';
import { Check, Circle, X } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomstructureset';
import { StructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { openedStructureSetApprovalConfirmationDialogState } from '~/state/atoms/structureSet/roiAtoms';
import StructureSetApprovalStatusConfirmationDialog from './StructureSetApprovalStatusConfirmationDialog';

interface ApprovalStatusDropdownProps {
    structureSet: StructureSetState;
    onApprovalStatusChange: (newApprovalStatus: DICOMApprovalStatus) => void;
}

export default function ApprovalStatusDropdown({
    structureSet,
    onApprovalStatusChange,
}: ApprovalStatusDropdownProps) {
    const { palette } = useTheme();

    const setOpenedStructureSetApprovalConfirmationDialog = useSetRecoilState(
        openedStructureSetApprovalConfirmationDialogState
    );

    const getApprovalIcon = (approvalStatus: DICOMApprovalStatus) => {
        switch (approvalStatus) {
            case DICOMApprovalStatus.APPROVED:
                return <Check size={16} color={palette.success.main} />;
            case DICOMApprovalStatus.REJECTED:
                return <X size={16} color={palette.error.main} />;
            default:
                return <Circle size={16} color={palette.text.secondary} />;
        }
    };

    const handleChange = (e: SelectChangeEvent<DICOMApprovalStatus>) => {
        const newApprovalStatus = e.target.value as DICOMApprovalStatus;

        // Only show confirmation dialog if the status is actually changing
        if (newApprovalStatus !== structureSet.approvalStatus) {
            setOpenedStructureSetApprovalConfirmationDialog({
                sopUid: structureSet?.sopUid,
                newApprovalStatus: newApprovalStatus,
            });
        }
    };

    return (
        <>
            <FormControl fullWidth size="small">
                <InputLabel id="approval-status-label" shrink>
                    <FormattedMessage
                        id="approvalStatus"
                        defaultMessage="Approval Status"
                    />
                </InputLabel>

                <Select
                    labelId="approval-status-label"
                    label={
                        <FormattedMessage
                            id="approvalStatus"
                            defaultMessage="Approval Status"
                        />
                    }
                    value={structureSet.approvalStatus}
                    data-testid="approval-status-dropdown"
                    onChange={handleChange}
                    renderValue={(selected) => (
                        <Box display="flex" alignItems="center" gap={1}>
                            {getApprovalIcon(selected)}
                            <Typography variant="body2">
                                <FormattedMessage id={selected.toLowerCase()} />
                            </Typography>
                        </Box>
                    )}
                >
                    {Object.values(DICOMApprovalStatus)
                        .filter((status) => status !== structureSet.approvalStatus)
                        .map((status) => (
                            <MenuItem
                                key={status}
                                value={status}
                                sx={{ fontSize: '0.875rem' }}
                                data-testid={`approval-status-option-${status.toLowerCase()}`}
                            >
                                <Box display="flex" alignItems="center" gap={1}>
                                    {getApprovalIcon(status)}
                                    <Typography variant="body2">
                                        <FormattedMessage
                                            id={status.toLowerCase()}
                                        />
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>

            <StructureSetApprovalStatusConfirmationDialog
                onApprovalStatusChange={onApprovalStatusChange}
            />
        </>
    );
}